import {
  Card,
  Badge,
  Text,
  Heading,
  Flex,
  CheckIcon,
  Button,
  InfinityIcon,
  Separator,
  Tooltip,
  CircleDollarSignIcon,
  Skeleton,
  InfoIcon,
  Link,
} from '@buffer-mono/popcorn'
import type { TooltipProps } from '@buffer-mono/popcorn'
import { useEffect, useState } from 'react'
import type { JSX } from 'react'

import type { ObPlanId, BillingInterval } from '../../../../gql/graphql'

import {
  FREE_PLAN_CHANNEL_LIMIT,
  AGENCY_PLAN_CHANNEL_MINIMUM,
} from '../../constants'
import {
  OBPlanIdValues,
  BillingIntervalValues,
  UpdatePlanPhase,
} from '../../types'
import { getVolumeDiscountMessage } from '../../utils/dataTransformers'
import type { ExtendedPlanOption } from '../../utils/dataTransformers'
import type { QueryDiscount } from '../../utils/discountCalculations'
import { formatPrice } from '../../utils/pricingCalculations'

import styles from './PlanCard.module.css'
import { useGetCustomInvoice } from '../../hooks/useGetCustomInvoice'
import { usePlanSelector } from '../../hooks/usePlanSelector'

interface PlanFeature {
  id: string
  name: string
  highlight?: boolean
}

function getPlanBaseFeatures(planId: ObPlanId): PlanFeature[] {
  switch (planId) {
    case OBPlanIdValues.free:
      return [
        { id: 'posts', name: '10 scheduled posts per channel' },
        { id: 'threads', name: '1 scheduled thread' },
        { id: 'ideas', name: '100 ideas' },
        { id: 'users', name: '1 user account' },
        { id: 'api', name: '1 API key & client' },
      ]
    case OBPlanIdValues.essentials:
      return [
        { id: 'posts', name: 'Unlimited posts, incl. threads' },
        { id: 'ideas', name: 'Unlimited ideas, tags and replies' },
        { id: 'users', name: '1 user account' },
        { id: 'api', name: '3 API keys & clients with more usage' },
      ]
    case OBPlanIdValues.team:
      return [
        { id: 'posts', name: 'Unlimited posts, incl. threads' },
        { id: 'ideas', name: 'Unlimited ideas, tags and replies' },
        { id: 'users', name: 'Unlimited users' },
        { id: 'api', name: '5 API keys & clients with more usage' },
      ]
    case OBPlanIdValues.agency:
      return [
        { id: 'posts', name: 'Unlimited posts, incl. threads' },
        { id: 'ideas', name: 'Unlimited ideas, tags and replies' },
        { id: 'users', name: 'Unlimited users' },
        { id: 'api', name: '5 API keys & clients with more usage' },
      ]
    default:
      return []
  }
}

function getPlanExtraFeatures(planId: ObPlanId): PlanFeature[] | undefined {
  switch (planId) {
    case OBPlanIdValues.free:
      return [
        { id: 'analytics', name: 'Basic analytics' },
        { id: 'community', name: 'Community' },
      ]
    case OBPlanIdValues.essentials:
      return [
        { id: 'analytics', name: 'Advanced analytics' },
        { id: 'community', name: 'Community' },
        { id: 'hashtags', name: 'Hashtag manager' },
        { id: 'comment', name: 'First comment' },
      ]
    case OBPlanIdValues.team:
      return [
        { id: 'access', name: 'Access levels' },
        { id: 'approval', name: 'Content approval workflows' },
        { id: 'notes', name: 'Notes on content' },
      ]
    case OBPlanIdValues.agency:
      return [{ id: 'pricing', name: 'Agency-friendly pricing' }]
    default:
      return undefined
  }
}

function getPlanExtraFeaturesTitle(planId: ObPlanId): string | undefined {
  switch (planId) {
    case OBPlanIdValues.team:
      return 'Everything in Essentials, plus:'
    case OBPlanIdValues.agency:
      return 'Everything in Team, plus:'
    default:
      return undefined
  }
}

/**
 * Formats discount duration text for user display
 */
function formatDiscountDuration(discount: QueryDiscount): string {
  switch (discount.duration) {
    case 'forever':
      return 'forever'

    case 'once':
      return 'on your next invoice'

    case 'repeating':
      return discount.durationInMonths
        ? `for ${discount.durationInMonths} months`
        : 'for a limited time'
    default:
      return ''
  }
}

/**
 * Gets promotional discount messages from applied discounts
 */
function getPromoDiscountMessages(appliedDiscounts: QueryDiscount[]): string[] {
  const messages: string[] = []

  appliedDiscounts.forEach((discount) => {
    const duration = formatDiscountDuration(discount)
    const durationText = duration ? ` ${duration}` : ''

    if (discount.__typename === 'PercentDiscount') {
      const percentage = discount.percentOff
      messages.push(`${percentage}% off${durationText} discount applied`)
    } else if (discount.__typename === 'AmountDiscount') {
      const amount = formatPrice(discount.amountOff)
      messages.push(`${amount} off${durationText} discount applied`)
    }
  })

  return messages
}

/**
 * Generates user-friendly discount copy text based on applied discounts and volume pricing tiers
 * @param appliedDiscounts - Array of discounts from Stripe/billing system
 * @param channelCount - Current number of channels user has in counter
 * @param planPricing - Pricing tier structure with upperBound values
 * @returns Array of discount description strings
 */
function generateDiscountCopy(
  appliedDiscounts: QueryDiscount[],
  channelCount: number,
  planPricing: ExtendedPlanOption['pricing'],
): string[] {
  const discountMessages: string[] = []

  // Add volume discount message if applicable
  const volumeDiscountMessage = getVolumeDiscountMessage(
    channelCount,
    planPricing,
  )
  if (volumeDiscountMessage) {
    discountMessages.push(volumeDiscountMessage)
  }

  // Add promotional discount messages
  const promoDiscountMessages = getPromoDiscountMessages(appliedDiscounts)
  discountMessages.push(...promoDiscountMessages)

  return discountMessages
}

export interface PlanCardProps {
  plan: ExtendedPlanOption
  channelCount: number
  billingCycle: BillingInterval
  onSelect: () => void | Promise<void>
  isCurrent?: boolean
  isUpgrade?: boolean
  isDowngrade?: boolean
  needsUpdate?: boolean
  isRecommended?: boolean
  isPayingViaMobile?: boolean
  onInvoiceError?: (planId: ObPlanId, error: Error) => void
  isAnyPlanUpdating?: boolean
  updatingPlanId?: ObPlanId | null
  updatePlanPhase?: UpdatePlanPhase
  isOnTrial?: boolean
  showTieredPricingCta?: boolean
  onTieredPricingCtaClick?: () => void
}

/**
 * A plan card component that displays pricing, features, and selection actions for a billing plan.
 * Shows appropriate action buttons based on the plan's relationship to the current plan
 * (upgrade, downgrade, update, or current).
 *
 * @param plan - Extended plan option with pricing calculations
 * @param channelCount - Number of channels for pricing display
 * @param billingCycle - Billing cycle for pricing display
 * @param onSelect - Callback function when plan is selected
 */
const PlanCard = ({
  plan,
  channelCount,
  billingCycle,
  onSelect,
  isCurrent = false,
  isUpgrade = false,
  isDowngrade = false,
  needsUpdate = false,
  isRecommended = false,
  isPayingViaMobile = false,
  onInvoiceError,
  isAnyPlanUpdating = false,
  updatingPlanId = null,
  updatePlanPhase = UpdatePlanPhase.Idle,
  isOnTrial = false,
  showTieredPricingCta = false,
  onTieredPricingCtaClick,
}: PlanCardProps): JSX.Element | null => {
  const { data, currentUser } = usePlanSelector()

  const [isUpgradeLoading, setIsUpgradeLoading] = useState(false)
  const [isDowngradeLoading, setIsDowngradeLoading] = useState(false)
  const [isUpdateLoading, setIsUpdateLoading] = useState(false)
  const [upgradeError, setUpgradeError] = useState<string | null>(null)
  const [downgradeError, setDowngradeError] = useState<string | null>(null)
  const [updateError, setUpdateError] = useState<string | null>(null)
  const isFree = plan.planId === OBPlanIdValues.free
  const isOnFreePlan = currentUser.currentPlan.planId === OBPlanIdValues.free
  const billingPeriod = isFree
    ? 'forever'
    : billingCycle === BillingIntervalValues.year
      ? 'year'
      : 'month'
  const priceText = formatPrice(plan.calculations?.totalPrice ?? 0)

  const planId = plan.planId
  const baseFeatures = getPlanBaseFeatures(planId)
  const extraFeatures = getPlanExtraFeatures(planId)
  const extraFeaturesTitle = getPlanExtraFeaturesTitle(planId)

  const isCanceled = currentUser.isChurning

  // Disable downgrade if the plan is free and the channel count is greater than the free plan limit, or if the plan is already canceled
  const disableDowngrade =
    plan.planId === OBPlanIdValues.free &&
    (channelCount > FREE_PLAN_CHANNEL_LIMIT || isCanceled)

  // Disable agency plan actions if channel count is less than minimum
  const disableAgencyUpgrade =
    plan.planId === OBPlanIdValues.agency &&
    channelCount < AGENCY_PLAN_CHANNEL_MINIMUM

  const getDowngradeTooltipContent = (): string => {
    if (isCanceled && plan.planId === OBPlanIdValues.free) {
      return `Your plan is already canceled. You can continue to use your ${currentUser.currentPlan.planName} features until the end of your billing cycle.`
    }
    return `Free plan supports up to ${FREE_PLAN_CHANNEL_LIMIT} channels. Remove extra channels to downgrade.`
  }

  const getAgencyUpgradeTooltipContent = (): string => {
    return `Agency plan requires at least ${AGENCY_PLAN_CHANNEL_MINIMUM} channels. Increase channel count to upgrade.`
  }

  const shouldShowYearlySavings = Boolean(
    billingCycle === BillingIntervalValues.year &&
    plan.calculations?.savings &&
    plan.calculations.savings > 0,
  )

  const yearlySavings =
    shouldShowYearlySavings && plan.calculations?.savings
      ? formatPrice(plan.calculations.savings)
      : ''

  const discountCopyList = generateDiscountCopy(
    plan.appliedDiscounts || [],
    channelCount,
    plan.pricing,
  )

  const handleUpgradeClick = async (): Promise<void> => {
    if (isPayingViaMobile) return
    setIsUpgradeLoading(true)
    setUpgradeError(null)
    try {
      await onSelect()
    } catch (error) {
      setUpgradeError(
        error instanceof Error ? error.message : 'An error occurred',
      )
    } finally {
      setIsUpgradeLoading(false)
    }
  }

  const handleDowngradeClick = async (): Promise<void> => {
    if (isPayingViaMobile) return
    setIsDowngradeLoading(true)
    setDowngradeError(null)
    try {
      await onSelect()
    } catch (error) {
      setDowngradeError(
        error instanceof Error ? error.message : 'An error occurred',
      )
    } finally {
      setIsDowngradeLoading(false)
    }
  }

  const handleUpdateClick = async (): Promise<void> => {
    if (isPayingViaMobile) return
    setIsUpdateLoading(true)
    setUpdateError(null)
    try {
      await onSelect()
    } catch (error) {
      setUpdateError(
        error instanceof Error ? error.message : 'An error occurred',
      )
    } finally {
      setIsUpdateLoading(false)
    }
  }

  const isThisCardUpdateInProgress =
    updatingPlanId === plan.planId && (isUpdateLoading || isAnyPlanUpdating)

  const {
    getCustomInvoice,
    data: customInvoiceData,
    loading: isDueNowLoading,
    error: invoiceError,
    clearData,
  } = useGetCustomInvoice({ showErrorToast: false })
  const dueNowAmount = customInvoiceData?.dueNow
  const isScheduledChange = customInvoiceData?.isScheduledChange

  const shouldShowDueNow =
    isDueNowLoading ||
    (dueNowAmount !== undefined && dueNowAmount > 0 && !isScheduledChange)

  useEffect(() => {
    // Don't fetch invoice preview for mobile billing users
    if (isPayingViaMobile) {
      clearData()
      return
    }

    // Clear data when it's not needed (not an upgrade or update)
    if (!data?.organizationId || (!isUpgrade && !needsUpdate)) {
      // Cancel any pending debounced calls before clearing
      if (getCustomInvoice.isPending()) {
        getCustomInvoice.cancel() // Cancel pending calls
      }
      clearData()
      return
    }

    if (!isOnFreePlan) {
      void getCustomInvoice({
        planId: plan.planId,
        interval: billingCycle,
        quantity: channelCount,
        organizationId: data.organizationId,
      })
    }
  }, [
    channelCount,
    billingCycle,
    plan.planId,
    data?.organizationId,
    getCustomInvoice,
    clearData,
    isUpgrade,
    needsUpdate,
    isPayingViaMobile,
  ])

  // Separate cleanup on unmount only
  useEffect(() => {
    return (): void => {
      // Cancel any pending calls and clear local data
      getCustomInvoice.cancel()
      clearData()
    }
  }, [getCustomInvoice, clearData])

  // Notify parent component when invoice query fails
  useEffect(() => {
    if (invoiceError && onInvoiceError) {
      onInvoiceError(plan.planId, invoiceError)
    }
  }, [invoiceError, plan.planId, onInvoiceError])

  // Defensive check: return null if plan data is incomplete
  if (!plan.calculations) {
    return null
  }

  return (
    <Card
      padding="large"
      className={`${styles.planCard} ${
        isRecommended ? styles.recommended : ''
      }`}
    >
      <Flex justify="between" align="center" className={styles.header}>
        <Heading size="md" as="h3">
          {plan.planName}
        </Heading>
        {isCurrent && (
          <Badge
            variant="success"
            size="small"
            className={styles.currentPlanBadge}
          >
            Current Plan
          </Badge>
        )}
      </Flex>

      <Flex direction="column" gap="md" fullWidth>
        <Flex direction="column" gap="md" fullWidth>
          <div>
            <Flex align="center" gap="xs">
              <Text weight="bold" className={styles.priceText}>
                {priceText}
              </Text>
              <Text size={discountCopyList.length > 0 ? 'sm' : 'md'}>
                / {billingPeriod}
                {shouldShowDueNow && (
                  <>
                    ,{' '}
                    <span className={styles.dueNowWrapper}>
                      <Skeleton show={isDueNowLoading}>
                        <>
                          <span>
                            {dueNowAmount !== undefined && !isDueNowLoading
                              ? `${formatPrice(dueNowAmount)} due now`
                              : 'Calculating...'}
                          </span>{' '}
                          <Tooltip content="Billing is prorated for mid-cycle changes">
                            <InfoIcon size="xsmall" />
                          </Tooltip>
                        </>
                      </Skeleton>
                    </span>
                  </>
                )}
                {!shouldShowDueNow && !isFree && ' + applicable tax'}
              </Text>
            </Flex>
            <Text size="sm" color="subtle">
              {isFree
                ? `Connect up to ${FREE_PLAN_CHANNEL_LIMIT} channels`
                : `${channelCount} ${
                    channelCount === 1 ? 'channel' : 'channels'
                  } • billed ${
                    billingCycle === BillingIntervalValues.year
                      ? 'yearly'
                      : 'monthly'
                  }`}
              {shouldShowYearlySavings && (
                <Text size="sm" weight="bold">
                  {' '}
                  ({yearlySavings} saved)
                </Text>
              )}
            </Text>
            {discountCopyList.length > 0 && (
              <Flex direction="column" gap="3xs">
                {discountCopyList.map((discountText, index) => (
                  <Text
                    size="sm"
                    className={styles.discountContainer}
                    key={index}
                    icon={<CircleDollarSignIcon size="xsmall" />}
                  >
                    {discountText}
                  </Text>
                ))}
              </Flex>
            )}
          </div>

          {isUpgrade && (
            <Flex direction="column" gap="xs" fullWidth>
              <ConditionalTooltip
                condition={disableAgencyUpgrade}
                content={getAgencyUpgradeTooltipContent()}
              >
                <Button
                  onClick={handleUpgradeClick}
                  variant="primary"
                  size="large"
                  className={styles.selectButton}
                  loading={isUpgradeLoading}
                  disabled={
                    disableAgencyUpgrade ||
                    isPayingViaMobile ||
                    isAnyPlanUpdating
                  }
                >
                  {updatingPlanId === plan.planId &&
                  (isUpgradeLoading || isAnyPlanUpdating)
                    ? updatePlanPhase === UpdatePlanPhase.Updating
                      ? 'Updating plan'
                      : updatePlanPhase === UpdatePlanPhase.Authorizing
                        ? 'Authorizing payment'
                        : updatePlanPhase === UpdatePlanPhase.Confirming
                          ? 'Confirming payment'
                          : isOnTrial
                            ? 'Start Subscription'
                            : `Upgrade to ${plan.planName}`
                    : isOnTrial
                      ? 'Start Subscription'
                      : `Upgrade to ${plan.planName}`}
                </Button>
              </ConditionalTooltip>
              {upgradeError && (
                <Text color="critical" size="sm">
                  {upgradeError}
                </Text>
              )}
            </Flex>
          )}

          {isDowngrade && (
            <Flex direction="column" gap="xs" fullWidth>
              <ConditionalTooltip
                condition={disableDowngrade}
                content={getDowngradeTooltipContent()}
              >
                <Button
                  onClick={handleDowngradeClick}
                  variant="secondary"
                  size="large"
                  disabled={
                    disableDowngrade || isPayingViaMobile || isAnyPlanUpdating
                  }
                  className={styles.selectButton}
                  loading={isDowngradeLoading}
                >
                  {isOnTrial && !isFree
                    ? 'Start Subscription'
                    : `Downgrade to ${plan.planName}`}
                </Button>
              </ConditionalTooltip>
              {downgradeError && (
                <Text color="critical" size="sm">
                  {downgradeError}
                </Text>
              )}
            </Flex>
          )}

          {needsUpdate && (
            <Flex direction="column" gap="xs" fullWidth>
              <ConditionalTooltip
                condition={disableAgencyUpgrade}
                content={getAgencyUpgradeTooltipContent()}
              >
                <Button
                  onClick={handleUpdateClick}
                  variant="primary"
                  size="large"
                  className={styles.selectButton}
                  loading={isThisCardUpdateInProgress}
                  disabled={
                    disableAgencyUpgrade ||
                    isPayingViaMobile ||
                    isAnyPlanUpdating
                  }
                >
                  {isThisCardUpdateInProgress
                    ? updatePlanPhase === UpdatePlanPhase.Updating
                      ? 'Updating plan'
                      : updatePlanPhase === UpdatePlanPhase.Authorizing
                        ? 'Authorizing payment'
                        : updatePlanPhase === UpdatePlanPhase.Confirming
                          ? 'Confirming payment'
                          : isOnTrial
                            ? 'Start Subscription'
                            : 'Update Plan'
                    : isOnTrial
                      ? 'Start Subscription'
                      : 'Update Plan'}
                </Button>
              </ConditionalTooltip>
              {updateError && (
                <Text color="critical" size="sm">
                  {updateError}
                </Text>
              )}
            </Flex>
          )}

          {/* Show CTA for trial users on their current plan with same channel count */}
          {isCurrent &&
            !needsUpdate &&
            !isUpgrade &&
            !isDowngrade &&
            isOnTrial && (
              <Flex direction="column" gap="xs" fullWidth>
                <Button
                  onClick={handleUpgradeClick}
                  variant="primary"
                  size="large"
                  className={styles.selectButton}
                  loading={isUpgradeLoading}
                  disabled={isPayingViaMobile || isAnyPlanUpdating}
                >
                  Start Subscription
                </Button>
                {upgradeError && (
                  <Text color="critical" size="sm">
                    {upgradeError}
                  </Text>
                )}
              </Flex>
            )}
        </Flex>

        <Flex direction="column" fullWidth>
          <Flex direction="column" gap="2xs">
            {baseFeatures.map((feature) => (
              <Text
                key={feature.id}
                color={feature.highlight ? 'brand' : 'inherit'}
                icon={
                  feature.name.toLocaleLowerCase().includes('unlimited') ? (
                    <InfinityIcon size="small" color="default" />
                  ) : (
                    <CheckIcon size="xsmall" color="default" />
                  )
                }
              >
                {feature.name}
              </Text>
            ))}
          </Flex>

          {extraFeatures && extraFeatures.length > 0 && (
            <>
              <Separator className={styles.separator} />
              {extraFeaturesTitle && (
                <Text
                  weight="bold"
                  size="sm"
                  className={styles.extraFeaturesTitle}
                >
                  {extraFeaturesTitle}
                </Text>
              )}
              <Flex direction="column" gap="2xs">
                {extraFeatures.map((feature) => (
                  <Text
                    key={feature.id}
                    color={feature.highlight ? 'brand' : 'inherit'}
                    icon={
                      feature.name.toLocaleLowerCase().includes('unlimited') ? (
                        <InfinityIcon size="small" color="default" />
                      ) : (
                        <CheckIcon size="xsmall" color="default" />
                      )
                    }
                  >
                    {feature.name}
                  </Text>
                ))}
              </Flex>
            </>
          )}

          {/* Tiered Pricing CTA for non-tiered users */}
          {showTieredPricingCta && (
            <>
              <Separator className={styles.separator} />
              <Text size="sm" color="subtle" align="center">
                Need more than 10 channels?{' '}
                <Link as="button" onClick={onTieredPricingCtaClick}>
                  Contact us for pricing
                </Link>
              </Text>
            </>
          )}
        </Flex>
      </Flex>
    </Card>
  )
}

function ConditionalTooltip({
  condition,
  content,
  children,
}: {
  condition: boolean
  content: TooltipProps['content']
  children: TooltipProps['children']
}): JSX.Element {
  return condition ? (
    <Tooltip content={content}>{children}</Tooltip>
  ) : (
    <>{children}</>
  )
}

export default PlanCard
